// Forms/Import DSL — grid changeset logic (Phase 2c-3). Pure functions (no UI),
// so they're node-testable: diff an edited grid against its loaded rows, then
// resolve the diff into action steps using the grid's Persist plan. The runtime
// posts the steps to the action endpoint (transactional).
//
// Working-row convention: each working row carries `__k` = the key value of the
// original row it came from, or null for a row the user added. Removed original
// keys are tracked separately.

// changeset diffs working rows against the originals.
//   originals : array of loaded row objects
//   working   : array of current row objects (each with __k = orig key | null)
//   removed   : array of original key values the user removed
//   keyCol    : the key column name
// → { added, edited, removed } (each an array of plain row objects, __k stripped).
export function changeset({ originals, working, removed, keyCol }) {
  const byKey = {};
  for (const r of originals || []) byKey[String(r[keyCol])] = r;

  const added = [];
  const edited = [];
  for (const w of working || []) {
    if (w.__k == null) {
      added.push(strip(w));
      continue;
    }
    const orig = byKey[String(w.__k)];
    // Carry the original row under __old so an `update` SP can bind :row.old.<col>
    // (the pre-edit value) alongside :row.<col> (the new value).
    if (orig && rowChanged(orig, w, keyCol)) edited.push({ ...strip(w), __old: strip(orig) });
  }
  const removedRows = (removed || []).map((k) => byKey[String(k)]).filter(Boolean);
  return { added, edited, removed: removedRows };
}

// resolveSteps turns a changeset into ordered action steps using a grid's Persist
// plan. Per row: before-<op> hooks → the persist step → after-<op> hooks, so a
// `require` veto or recalc runs in the same transaction. `formBind(path)`
// resolves a non-row bind (e.g. :jual) to a value.
export function resolveSteps(persist, cs, formBind) {
  if (!persist) return [];
  const steps = [];
  const hooks = persist.hooks || {};
  const fire = (key, row) => { for (const s of hooks[key] || []) pushStep(steps, s, row, formBind); };
  for (const row of cs.added) { fire('before-insert', row); pushStep(steps, persist.insert, row, formBind); fire('after-insert', row); }
  for (const row of cs.edited) { fire('before-update', row); pushStep(steps, persist.update, row, formBind); fire('after-update', row); }
  for (const row of cs.removed) { fire('before-delete', row); pushStep(steps, persist.delete, row, formBind); fire('after-delete', row); }
  return steps;
}

// pushStep appends one resolved server step (call/exec/require). Client-only ops
// (toast/close/refresh) inside a hook are skipped — they don't run server-side.
function pushStep(steps, pstep, row, formBind) {
  if (!pstep || (pstep.op !== 'call' && pstep.op !== 'exec' && pstep.op !== 'require')) return;
  const binds = {};
  for (const path of pstep.binds || []) {
    if (path.startsWith('row.old.')) binds[path] = row.__old ? (row.__old[path.slice(8)] ?? null) : null;
    else if (path.startsWith('row.')) binds[path] = row[path.slice(4)];
    else binds[path] = formBind ? formBind(path) : null;
  }
  const step = { op: pstep.op, sql: pstep.sql, binds };
  if (pstep.message) step.message = pstep.message;
  steps.push(step);
}

function rowChanged(orig, work, keyCol) {
  for (const k in work) {
    if (k === '__k' || k === keyCol) continue;
    if (String(orig[k] ?? '') !== String(work[k] ?? '')) return true;
  }
  return false;
}

function strip(row) {
  const o = {};
  for (const k in row) if (k !== '__k') o[k] = row[k];
  return o;
}
